/* Copyright (c) 2020-present CloudIO Inc */

import { Theme, InputBase, IconButton, Divider } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useStateXAtom } from '@cloudio-saas/statex';
import React, { useRef } from 'react';
import Icon from 'icons/Icon';
import { useStoreDirty, useStoreBusy } from 'store/useAppStore';
import WaveDots from 'utils/WaveDots';
import ExpandCollapseIcon from 'layout/Sidebar/panels/ExpandCollapseIcon';
import { DSView, sidebarDataSourceAccessFilterAtom } from '../../atoms';
import { useIoDatasourceAccessVStore } from 'store/utils/utils';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '2px 4px',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  iconButton: {
    padding: 10,
  },
  divider: {
    height: 28,
    margin: 4,
  },
}));

export default function DataSourceAccessHeader({
  setView,
  appUid,
}: {
  appUid: string;
  setView: (_view: DSView) => void;
}): React.ReactElement {
  const store = useIoDatasourceAccessVStore(appUid);
  const dirty = useStoreDirty(store);
  const busy = useStoreBusy(store);
  const classes = useStyles();
  const [search, setSearch] = useStateXAtom(sidebarDataSourceAccessFilterAtom);
  const ref = useRef<HTMLInputElement>(null);

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearch(e.target.value);
  }

  return (
    <div className={classes.root}>
      <IconButton
        data-testid="back"
        onClick={() => {
          store.reset();
          setView('list');
        }}
        className={classes.iconButton}
        aria-label="Back"
      >
        <Icon iconName="chevron-left" />
      </IconButton>

      <InputBase
        inputRef={ref}
        className={classes.input}
        placeholder="Filter Roles"
        inputProps={{ 'aria-label': 'filter roles' }}
        value={search}
        onChange={onChange}
        autoFocus
      />

      <Divider className={classes.divider} orientation="vertical" />
      {dirty ? (
        <IconButton
          data-testid="save"
          disabled={busy}
          color="primary"
          aria-label="Save"
          onClick={async () => {
            const r = await store.save({
              feedback: '#{count} DataSource Access updated successfully!',
            });
            if (r) {
              setView('list');
            }
          }}
        >
          {busy ? (
            <WaveDots active />
          ) : (
            <Icon color="primary" iconName="check" />
          )}
        </IconButton>
      ) : null}
      <ExpandCollapseIcon />
    </div>
  );
}
